import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AssociateService } from './associate.service';

export interface Associate {
  id: number;
  collegeName: string;
  location: string;
  students: number;
  category: string;
  whiteLabelled: boolean;
}


@Component({
  selector: 'app-associates',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './associates.component.html',
  styleUrl: './associates.component.css'
})
export class AssociatesComponent implements OnInit {

  associates: Associate[] = [];
  filteredAssociates: Associate[] = [];
  selectedAssociates: Associate[] = [];
  loading = false;
  searchTerm = '';
  locationFilter = '';
  categoryFilter = '';
  whiteLabelledFilter = '';
  studentRangeFilter = '';
  customStudentMin: number | null = null;
  customStudentMax: number | null = null;
  customStudentMinInput = '';
  customStudentMaxInput = '';

  locations: string[] = ['All', 'Pune', 'Mumbai', 'Jalgaon'];
  categories: string[] = ['All', 'Management', 'Engineering', 'Arts', 'Science', 'Commerce'];
  whiteLabelledOptions: string[] = ['All', 'Yes', 'No'];

  studentRanges: string[] = ['All', 'upto300', '300-700', '700-1000', '1000-1300', 'custom'];

  studentRangeLabels: Record<string, string> = {
    All: 'All',
    upto300: 'upto 300',
    '300-700': '300-700',
    '700-1000': '700-1000',
    '1000-1300': '1000-1300',
    custom: 'Custom'
  };


constructor(private associateService: AssociateService, private router: Router) {}

  onEditAssociate(id: number): void {
    this.router.navigate(['/associates/create-associate', id]);
  }

  onAnalytics(id: number): void {
    // Analytics tab exists inside CreateAssociateComponent (activeTab === 4)
    this.router.navigate(['/associates/create-associate', id], { queryParams: { tab: 'analytics', tabIndex: 4 } });

  }

  onDeleteAssociate(id: number): void {

    const ok = window.confirm('Are you sure you want to delete this associate?');
    if (!ok) return;

    this.associateService.deleteClientById(id).subscribe({
      next: () => {
        this.getAssociates();
      },
      error: (err) => {
        console.error('Failed to delete associate:', err);
      }
    });
  }
  ngOnInit(): void {
    this.getAssociates();
  }

  getAssociates(): void {
    this.loading = true;

    this.associateService.getAllAssociates().subscribe({
      next: (res: any) => {
        const raw = Array.isArray(res)
          ? res
          : (res?.data ?? res?.clients ?? res?.associates ?? []);
        this.associates = (raw as any[]).map((c: any) => {
          const city = c?.city ?? '';
          const state = c?.state ?? '';
          const country = c?.country ?? '';
          const location = [city, state, country].filter(Boolean).join(', ') || '';
          const selectedCourses = c?.selected_courses;
          const derivedStudents = Array.isArray(selectedCourses)
            ? selectedCourses.length
            : 0;

          return {
            id: Number(c?.id),
            collegeName: c?.client_company_name ?? '',
            location,
            students: derivedStudents,
            category: c?.category ?? '',
            whiteLabelled: Boolean(c?.white_labelled),
          } satisfies Associate;
        });

        this.filteredAssociates = [...this.associates];
        this.applyFilters();
      },
      error: (err) => {
        console.error('Error fetching associates:', err);
        this.associates = [];
        this.filteredAssociates = [];
      },
      complete: () => {
        this.loading = false;
      }
    });
  }


  onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.applyFilters();
  }

  onFilterChange(): void {
    if (this.studentRangeFilter !== 'custom') {
      this.customStudentMin = null;
      this.customStudentMax = null;
      this.customStudentMinInput = '';
      this.customStudentMaxInput = '';
    }
    this.applyFilters();
  }

  onCustomStudentRangeChange(): void {
    const minVal = this.customStudentMinInput.trim();
    const maxVal = this.customStudentMaxInput.trim();

    const minParsed = minVal === '' ? null : Number(minVal);
    const maxParsed = maxVal === '' ? null : Number(maxVal);

    this.customStudentMin = minParsed !== null && !isNaN(minParsed) ? minParsed : null;
    this.customStudentMax = maxParsed !== null && !isNaN(maxParsed) ? maxParsed : null;

    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredAssociates = this.associates.filter((associate) => {

      const matchesSearch =
        !this.searchTerm ||
        associate.collegeName
          ?.toLowerCase()
          .includes(this.searchTerm.toLowerCase());

     const matchesLocation =
  !this.locationFilter ||
  associate.location
    ?.toLowerCase()
    .includes(this.locationFilter.toLowerCase());

      const matchesCategory =
        !this.categoryFilter ||
        this.categoryFilter === 'All' ||
        associate.category === this.categoryFilter;

      const matchesWhiteLabelled =
        !this.whiteLabelledFilter ||
        this.whiteLabelledFilter === 'All' ||
        (this.whiteLabelledFilter === 'Yes' && associate.whiteLabelled === true) ||
        (this.whiteLabelledFilter === 'No' && associate.whiteLabelled === false);

      const matchesStudentRange = (() => {
        if (!this.studentRangeFilter || this.studentRangeFilter === 'All') return true;
        const n = associate.students ?? 0;

        switch (this.studentRangeFilter) {
          case 'upto300':
            return n >= 0 && n <= 300;
          case '300-700':
            return n >= 300 && n <= 700;
          case '700-1000':
            return n >= 700 && n <= 1000;
          case '1000-1300':
            return n >= 1000 && n <= 1300;
          case 'custom': {
            const minN = this.customStudentMin;
            const maxN = this.customStudentMax;
            if (minN === null && maxN === null) return true;
            if (minN !== null && maxN !== null) return n >= minN && n <= maxN;
            if (minN !== null) return n >= minN;
            // maxN is guaranteed non-null here because (minN===null && maxN===null) is handled above
            return maxN !== null ? n <= maxN : true;
          }
          default:
            return true;
        }
      })();

      return (
        matchesSearch &&
        matchesLocation &&
        matchesCategory &&
        matchesWhiteLabelled &&
        matchesStudentRange
      );
    });
  }


  onCheckboxChange(associate: Associate, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectedAssociates = [...this.selectedAssociates, associate];
    } else {
      this.selectedAssociates = this.selectedAssociates.filter(
        a => a.id !== associate.id
      );
    }
  }

  isSelected(associate: Associate): boolean {
    return this.selectedAssociates.some(
      a => a.id === associate.id
    );
  }

  toggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectedAssociates = [...this.filteredAssociates];
    } else {
      this.selectedAssociates = [];
    }
  }

  isAllSelected(): boolean {
    return (
      this.filteredAssociates.length > 0 &&
      this.selectedAssociates.length === this.filteredAssociates.length
    );
  }
}